import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { knowledgeBaseApi } from "@/api/knowledgeBase";
import { KbAskTab } from "@/components/knowledgeBase/KbAskTab";
import { KbBrowseTab } from "@/components/knowledgeBase/KbBrowseTab";
import { cn } from "@/design-system/lib/utils";

// Knowledge Base v1.0 PR 4 — landing page. Two tabs (Ask + Browse), each
// gated on its own permission. Users without either permission shouldn't
// reach the page (the App.tsx route is wrapped in <RequireAuth> with
// hasAnyPermission), but the tab strip filters individually so a user with
// only Ask sees just the Ask tab.

type TabKey = "ask" | "browse";

export function KnowledgeBasePage() {
  const { hasPermission } = useAuth();
  const canAsk = hasPermission("KnowledgeBase.Ask");
  const canBrowse = hasPermission("KnowledgeBase.BrowseLibrary");

  const initialTab: TabKey = canAsk ? "ask" : "browse";
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  const accessQ = useQuery({
    queryKey: ["kb", "my-access"],
    queryFn: () => knowledgeBaseApi.myAccess(),
    staleTime: 5 * 60_000,
  });

  const categoriesQ = useQuery({
    queryKey: ["kb", "categories"],
    queryFn: () => knowledgeBaseApi.listCategories(),
    staleTime: 5 * 60_000,
  });

  const tagsQ = useQuery({
    queryKey: ["kb", "tags"],
    queryFn: () => knowledgeBaseApi.listTags(),
    staleTime: 5 * 60_000,
    enabled: canBrowse,
  });

  if (!canAsk && !canBrowse) {
    return (
      <div className="max-w-reading mx-auto p-space-5 text-center">
        <h2 className="font-display font-bold text-h2">Access denied</h2>
        <p className="text-body text-gray-mid mt-space-3">
          Your role does not have permission to use the knowledge base.
        </p>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string; visible: boolean }[] = [
    { key: "ask", label: "Ask", visible: canAsk },
    { key: "browse", label: "Browse", visible: canBrowse },
  ];
  const visibleTabs = tabs.filter((t) => t.visible);

  return (
    <>
      <PageHeader
        eyebrow="Knowledge Base"
        title="Knowledge Base"
        description="Ask questions answered from Phoenix Capital's curated documents, or browse the gated document library."
      />

      {visibleTabs.length > 1 && (
        <div
          role="tablist"
          aria-label="Knowledge base sections"
          className="flex items-center gap-space-1 border-b border-border-light mb-space-4"
        >
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "px-space-3 py-space-2 font-display text-label font-semibold uppercase tracking-label",
                "transition-colors duration-fast ease-standard",
                activeTab === tab.key
                  ? "text-gold border-b-[2px] border-gold -mb-px"
                  : "text-gray-mid hover:text-onyx",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div role="tabpanel">
        {activeTab === "ask" && canAsk && (
          <KbAskTab
            myAccess={accessQ.data}
            categories={(categoriesQ.data ?? []).map((c) => ({
              code: c.code,
              name: c.name,
            }))}
          />
        )}
        {activeTab === "browse" && canBrowse && (
          <KbBrowseTab
            categories={categoriesQ.data ?? []}
            tags={tagsQ.data ?? []}
          />
        )}
      </div>
    </>
  );
}
